import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import theme from "assets/theme";
import themeDark from "assets/theme-dark";
import { AuthContext } from "context/auth-context/auth.context";
import RequireAuth from "context/auth-context/require-auth.provider";
import { SecurityProvider, useSecurity } from "context/security-context/security.context";
import { ChatProvider, useChatContext } from "context/chat-context/chat.context";
import {
  setMiniSidenav,
  useMaterialUIController,
} from "context/materialUI-context/materialUI.context";
import Sidenav from "components/Sidenav";
import { useGetClientById } from "pages/client/client.hook";
import { JSXElementConstructor, Key, lazy, ReactElement, Suspense, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import routes from "routes";
import { SELECT_CUSTOMERS, SELECT_FEATURES } from "types/client.type";
import LoadingPage from "components/LoadingPage";

// Lazy loaded components
const LiveChat = lazy(() => import("components/LiveChat"));
const SubscriptionPage = lazy(() => import("pages/subscription/SubscriptionPage"));
const SubscriptionTestPanel = lazy(() => import("components/SubscriptionTestPanel"));
const ClientSubscriptionPage = lazy(() => import("pages/client/subscription/ClientSubscriptionPage"));

const AppContent = () => {
  const { t } = useTranslation();
  const { chatMode } = useChatContext();

  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, direction, layout, sidenavColor, darkMode } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { currentAdmin, isAuthenticated, isLoading } = useContext(AuthContext);
  const { data: clientData } = useGetClientById(SELECT_FEATURES);
  const { data: customerData } = useGetClientById(SELECT_CUSTOMERS);

  const { isSuperAdmin } = useSecurity();

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // Détecter le redimensionnement de la fenêtre
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Redirection logic
  useEffect(() => {
    if (
      isAuthenticated &&
      currentAdmin?.emailVerified &&
      window.location.pathname.startsWith("/authentication")
    ) {
      navigate("/");
    }

    if (
      currentAdmin &&
      currentAdmin.roleByClient?.length &&
      window.location.pathname.startsWith("/create/app") &&
      !isSuperAdmin
    ) {
      navigate("/");
    }
  }, [currentAdmin, isAuthenticated, navigate, isSuperAdmin]);

  useEffect(() => {
    if (currentAdmin) {
      if (!isSuperAdmin && !currentAdmin.roleByClient?.length) navigate("/create/app");
    }
  }, [currentAdmin, isSuperAdmin, navigate]);

  const getRoutes = (allRoutes: any[]): any =>
    allRoutes.map(
      (route: {
        collapse: any;
        route: string;
        component: ReactElement<any, string | JSXElementConstructor<any>>;
        key: Key;
        privateRoute: boolean;
      }) => {
        if (route.collapse) {
          return getRoutes(route.collapse);
        }

        if (route.route) {
          if (route.privateRoute) {
            return (
              <Route
                path={route.route}
                element={<RequireAuth>{route.component}</RequireAuth>}
                key={route.key}
              />
            );
          } else return <Route path={route.route} element={route.component} key={route.key} />;
        }

        return null;
      }
    );

  // Afficher un loading pendant la vérification de l'auth
  if (isLoading) {
    return <LoadingPage message={t("app.loading")} />;
  }

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {chatMode === 'fullscreen' && isMobile ? (
        // Mobile fullscreen: chat prend tout l'espace
        <Suspense fallback={<LoadingPage message={t("app.loading")} />}>
          <LiveChat />
        </Suspense>
      ) : (
        // Mode normal, minimized ou fullscreen desktop
        <>
          {layout === "dashboard" && currentAdmin && currentAdmin.roleByClient?.length && (
            <>
              <Sidenav
                color={sidenavColor}
                brand={customerData?.logo || null}
                codeClient={customerData?.code || null}
                brandName={customerData?.name || t("app.loading")}
                routes={routes(currentAdmin, clientData?.features)}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
                isSuperAdmin={isSuperAdmin}
              />
            </>
          )}
          <Suspense fallback={<LoadingPage message={t("app.loading")} />}>
            <Routes>
              {getRoutes(routes(currentAdmin, clientData?.features))}
              {/* Subscription routes */}
              <Route path="/subscription" element={<SubscriptionPage />} />
              <Route path="/my-subscription" element={<ClientSubscriptionPage />} />
              <Route path="/subscription-test" element={<SubscriptionTestPanel />} />
              {currentAdmin && currentAdmin.roleByClient?.length ? (
                <Route path="*" element={<Navigate to="/myApp" />} />
              ) : (
                <Route path="*" element={<Navigate to="/authentication/sign-in" />} />
              )}
            </Routes>
          </Suspense>

          {/* LiveChat Component - Only shows when HOPELIA feature flag is enabled */}
          <Suspense fallback={null}>
            <LiveChat />
          </Suspense>
        </>
      )}
    </ThemeProvider>
  );
};

const App = () => {
  return (
    <SecurityProvider>
      <ChatProvider>
        <AppContent />
      </ChatProvider>
    </SecurityProvider>
  );
};

export default App;
